/**
 * DailyRewardsBottomSheet
 *
 * Single button-triggered surface for the daily-rewards feature (replaces the
 * always-on landing overlays — BUG-20260610-01). Mirrors the Leaderboard
 * button → BottomSheet pattern. Presentational sub-component: data + actions
 * come from `useDailyRewardsViewModel`, owned by the landing View.
 *
 * Story 01-02: the top section now renders a 4-state status header + hero card
 * driven by `state.sheetState`. The podium section (LE TOP #3 DU JOUR) is
 * unchanged below.
 *
 * Content order:
 *   1. SheetTopContent — status header (subtitle + status line) + hero banner card.
 *   2. Podium history (read-only closed days) with a day selector.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '@/shared/components/BottomSheet';
import { PillButton } from '@/shared/components/PillButton';
import { coinImg } from '@/assets';
import { createResponsiveTextStroke } from '@/styles/textStroke';
import type { DailyRewardsState, DailyRewardsActions } from '../hooks/useDailyRewardsViewModel';
import { DailyRewardClaimDialog } from './DailyRewardClaimDialog';
import PodiumList from './PodiumList';

export interface DailyRewardsBottomSheetProps {
  /** Whether the sheet is visible */
  isOpen: boolean;
  /** Dismiss callback */
  onClose: () => void;
  /** Daily-rewards ViewModel state (from the landing View) */
  state: DailyRewardsState;
  /** Daily-rewards ViewModel actions (from the landing View) */
  actions: DailyRewardsActions;
  /** Caller's display name — shown on the rank card + used to highlight their podium row */
  username: string;
}

/** Small section title on the blue sheet background (white, mind-boggle). */
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="font-mind-boggle text-white text-xl text-center uppercase"
      style={{ letterSpacing: '0.03em', textShadow: '1px 1px 0 rgba(0,0,0,0.3)' }}
    >
      {children}
    </h3>
  );
}

/** Per-state subtitle + status line rendered above the hero card. */
interface SheetTopContentProps {
  state: DailyRewardsState;
  actions: DailyRewardsActions;
  onOpenClaimModal: () => void;
}

function SheetTopContent({ state, actions, onOpenClaimModal }: SheetTopContentProps) {
  const { t } = useTranslation();
  const { sheetState, rank, lastResult, claimable, validMatches, eligibilityMin, countdownLabel } =
    state;

  // Subtitle and status-line text vary by state
  let subtitle: string;
  let statusLine: string;

  if (sheetState === 'A') {
    subtitle = t('dailyRewards.sheet.statusStartPlaying');
    statusLine = t('dailyRewards.sheet.lineCountdown', { countdown: countdownLabel });
  } else if (sheetState === 'B') {
    subtitle = t('dailyRewards.sheet.statusYourRank', { rank: rank ?? 0 });
    statusLine = t('dailyRewards.sheet.lineCountdown', { countdown: countdownLabel });
  } else if (sheetState === 'C') {
    subtitle = t('dailyRewards.sheet.statusFinished', { rank: lastResult?.rank ?? 0 });
    statusLine = t('dailyRewards.sheet.lineClosedYesterday');
  } else {
    // sheetState === 'D'
    subtitle = t('dailyRewards.sheet.statusFinished', { rank: claimable?.rank ?? 0 });
    statusLine = t('dailyRewards.sheet.lineClosedYesterday');
  }

  // Hero message varies by state
  const heroMessage =
    sheetState === 'C'
      ? t('dailyRewards.sheet.heroFinishedNoReward', { rank: lastResult?.rank ?? 0 })
      : sheetState === 'D'
        ? t('dailyRewards.sheet.heroWinner', { rank: claimable?.rank ?? 0 })
        : t('dailyRewards.sheet.heroPlay');

  // CTA variant and disabled state
  const ctaDisabled = sheetState === 'A' || sheetState === 'B';
  const ctaVariant =
    sheetState === 'C'
      ? ('blue' as const)
      : sheetState === 'D'
        ? ('green' as const)
        : ('grey' as const);
  const ctaLabel =
    sheetState === 'C' ? t('dailyRewards.sheet.acknowledge') : t('dailyRewards.claimModal.confirm');

  function handleCta() {
    if (sheetState === 'C' && lastResult != null) {
      actions.acknowledgeDay(lastResult.day);
    } else if (sheetState === 'D') {
      onOpenClaimModal();
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Status header — centered subtitle + countdown line */}
      <div className="flex flex-col gap-0.5">
        <p
          data-testid="sheet-subtitle"
          className="font-mind-boggle text-xl text-center text-white"
          style={{ letterSpacing: '0.03em', textShadow: '1px 1px 0 rgba(0,0,0,0.3)' }}
        >
          {subtitle}
        </p>
        <p
          data-testid="sheet-status-line"
          className="font-mind-boggle text-sm text-center"
          style={{ letterSpacing: '0.03em', color: '#A7DFFC' }}
        >
          {statusLine}
        </p>
      </div>

      {/* Hero banner card — 2 columns: message + progress (left), coin + CTA (right) */}
      <div
        data-testid="hero-card"
        className="w-full rounded-xl px-4 py-3 flex items-stretch gap-3"
        style={{ backgroundColor: '#E6F4FF', border: '2px solid black' }}
      >
        {/* Left — message (top) + progress (bottom) */}
        <div className="flex flex-1 min-w-0 flex-col justify-between gap-3">
          <p
            className="font-mind-boggle text-base leading-snug break-words"
            style={{ color: '#A7DFFC', ...createResponsiveTextStroke(1, 0.06) }}
          >
            {heroMessage}
          </p>
          <p
            data-testid="hero-progress"
            className="font-mind-boggle text-sm"
            style={{ color: '#1D4161' }}
          >
            {t('dailyRewards.sheet.progressMatches', {
              count: validMatches,
              total: eligibilityMin
            })}
          </p>
        </div>

        {/* Right — coin (top) + CTA (bottom) */}
        <div className="flex w-36 shrink-0 flex-col items-center justify-between gap-3">
          <img src={coinImg} alt="" className="h-20 w-20 object-contain" />
          <PillButton
            type="button"
            variant={ctaVariant}
            data-testid="hero-cta"
            disabled={ctaDisabled}
            onClick={handleCta}
            className="w-full h-12 [&_span]:!text-lg"
          >
            {ctaLabel}
          </PillButton>
        </div>
      </div>
    </div>
  );
}

export function DailyRewardsBottomSheet({
  isOpen,
  onClose,
  state,
  actions,
  username
}: DailyRewardsBottomSheetProps) {
  const { t } = useTranslation();
  const { loadPodium } = actions;

  // Day pending claim confirmation (null = dialog closed).
  const [confirmDay, setConfirmDay] = useState<string | null>(null);

  // Lazy-load today's top-3 leaderboard when the sheet is opened (and on re-open).
  useEffect(() => {
    if (isOpen) {
      loadPodium(state.day);
    }
  }, [isOpen, loadPodium, state.day]);

  // Close the confirm dialog if the sheet itself is dismissed.
  useEffect(() => {
    if (!isOpen) {
      setConfirmDay(null);
    }
  }, [isOpen]);

  function openClaimModal() {
    if (state.claimable != null) {
      setConfirmDay(state.claimable.day);
    }
  }

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={t('dailyRewards.sheet.title')}
        className="!p-2"
      >
        <div className="flex flex-col gap-4">
          {/* Section 1 — Top content: status header + per-state hero card */}
          <section aria-label={t('dailyRewards.sheet.todayTitle')}>
            <SheetTopContent state={state} actions={actions} onOpenClaimModal={openClaimModal} />
          </section>

          {/* Section 2 — LE TOP #3 DU JOUR: today's live top-3 leaderboard (by matches) */}
          <section aria-label={t('dailyRewards.sheet.top3Title')} className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <SectionHeader>{t('dailyRewards.sheet.top3Title')}</SectionHeader>
              <p
                className="font-mind-boggle text-sm text-center"
                style={{ letterSpacing: '0.03em', color: '#A7DFFC' }}
              >
                {t('dailyRewards.sheet.lineCountdown', { countdown: state.countdownLabel })}
              </p>
            </div>

            <PodiumList
              entries={state.podium?.entries}
              isLoading={state.isPodiumLoading}
              isEnabled={state.isRewardsEnabled}
              error={state.myDayError}
              t={t}
              metric="matches"
              currentUserPseudo={username}
              currentUserEntry={state.podium?.currentUserEntry}
            />
          </section>
        </div>
      </BottomSheet>

      {/* Confirmation step before claiming — sibling of the sheet so the modal
          overlay covers the page (Dialog uses absolute inset-0, not a portal). */}
      <DailyRewardClaimDialog
        isOpen={confirmDay !== null}
        amount={state.claimable?.amount ?? 0}
        isClaiming={state.isClaiming}
        onClose={() => {
          setConfirmDay(null);
        }}
        onConfirm={() => {
          if (confirmDay !== null) {
            void actions.claim(confirmDay);
          }
          setConfirmDay(null);
        }}
      />
    </>
  );
}

export default DailyRewardsBottomSheet;
